import { AutoLinkPlugin } from '@lexical/react/LexicalAutoLinkPlugin';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { EditorRefPlugin } from '@lexical/react/LexicalEditorRefPlugin';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { MarkdownShortcutPlugin } from '@lexical/react/LexicalMarkdownShortcutPlugin';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { TablePlugin } from '@lexical/react/LexicalTablePlugin';
import type { LexicalEditor, SerializedEditorState } from 'lexical';
import { useEffect, useRef } from 'react';
import type { UseControlledStateProps } from '../../../lib/hooks/use-controlled-state.ts';
import { useControlledState } from '../../../lib/hooks/use-controlled-state.ts';
import type { AIActionType } from '../hooks/use-improve-writing';
import type { EntityType } from '../types/entity-types';
import { AutoFocusPlugin } from './auto-focus-plugin';
import { ToolbarPlugin } from './toolbar-plugin';

import {
    BOLD_ITALIC_STAR,
    BOLD_ITALIC_UNDERSCORE,
    BOLD_STAR,
    BOLD_UNDERSCORE,
    ITALIC_STAR,
    ITALIC_UNDERSCORE,
    LINK,
    ORDERED_LIST,
    STRIKETHROUGH,
    UNORDERED_LIST,
} from '@lexical/markdown';
import { RICH_TEXT_NODES, RICH_TEXT_THEME } from '@plato/shared-util/lexical-config';

const TRANSFORMERS = [
    UNORDERED_LIST,
    ORDERED_LIST,
    BOLD_ITALIC_STAR,
    BOLD_ITALIC_UNDERSCORE,
    BOLD_STAR,
    BOLD_UNDERSCORE,
    ITALIC_STAR,
    ITALIC_UNDERSCORE,
    STRIKETHROUGH,
    LINK,
];

const URL_MATCHER =
    /((https?:\/\/(www\.)?)|(www\.))[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)/;

const MATCHERS = [
    (text: string) => {
        const match = URL_MATCHER.exec(text);
        if (match === null) {
            return null;
        }
        const fullMatch = match[0];
        return {
            index: match.index,
            length: fullMatch.length,
            text: fullMatch,

            url: fullMatch.startsWith('http') ? fullMatch : `https://${fullMatch}`,
            attributes: { rel: 'noreferrer', target: '_blank' },
        };
    },
];

export type RichTextValue = SerializedEditorState;

type RichTextViewerProps = {
    editable?: boolean;
    disableVoiceRecording?: boolean;
    autoFocus?: boolean;
    onChange?: (value: RichTextValue) => void;
    content: RichTextValue | null | undefined;
    contentEditable: JSX.Element;
    entityId?: string;
    /** Entity type to enable/disable AI improve-writing features */
    entityType?: EntityType;
    /** Controlled AI writing state (optional) */
    aiWritingState?: UseControlledStateProps<AIWritingState>['state'];
    /** Controlled AI writing state change handler (optional) */
    setAIWritingState?: UseControlledStateProps<AIWritingState>['setState'];
    /** Callback for AI actions with action type and sessionId */
    onAIInteraction?: (action: AIActionType, sessionId: string) => void;
};

export type AIWritingState = 'idle' | 'improve-loading' | 'improve-preview' | 'improve-error';

export const Lexical: React.FC<RichTextViewerProps> = ({
    disableVoiceRecording,
    content,
    editable = false,
    autoFocus = false,
    entityId,
    entityType,
    onChange,
    contentEditable,
    onAIInteraction,
    ...props
}) => {
    const editorRef = useRef<LexicalEditor>(null);
    const [aiWritingState, setAIWritingState] = useControlledState<AIWritingState>({
        state: props.aiWritingState,
        setState: props.setAIWritingState,
        defaultState: 'idle',
    });

    const initialConfig = {
        namespace: 'RichTextViewer',
        nodes: RICH_TEXT_NODES,
        editable,
        editorState: content ? JSON.stringify(content) : undefined,
        theme: RICH_TEXT_THEME,
        onError: (error: Error) => {
            console.error('RichTextViewer error:', error);
        },
    };

    useEffect(() => {
        const editor = editorRef.current;

        if (editable || !editor) {
            return;
        }

        editor.update(() => {
            try {
                editor.setEditorState(editor.parseEditorState(JSON.stringify(content)));
            } catch (error) {
                console.error('Error parsing editor state:', error);
            }
        });
    }, [content, editable]);

    return (
        <LexicalComposer initialConfig={initialConfig}>
            <RichTextPlugin contentEditable={contentEditable} ErrorBoundary={LexicalErrorBoundary} />
            {editable && (
                <ToolbarPlugin
                    disableVoiceRecording={disableVoiceRecording}
                    entityType={entityType}
                    entityId={entityId}
                    aiWritingState={aiWritingState}
                    setAIWritingState={setAIWritingState}
                    onChange={onChange}
                    onAIInteraction={onAIInteraction}
                />
            )}
            <ListPlugin />
            <LinkPlugin />
            <TablePlugin />
            <HistoryPlugin />
            <MarkdownShortcutPlugin transformers={TRANSFORMERS} />
            <AutoLinkPlugin matchers={MATCHERS} />
            {onChange && aiWritingState === 'idle' && (
                <OnChangePlugin
                    ignoreSelectionChange
                    onChange={(editor) => {
                        onChange(editor.toJSON());
                    }}
                />
            )}
            {editable && autoFocus && <AutoFocusPlugin />}
            <EditorRefPlugin editorRef={editorRef} />
        </LexicalComposer>
    );
};
